
import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';

const DEMO_IMAGES = [
  {
    url: 'https://public.readdy.ai/ai/img_res/fe72e9a7e344288bda31f59d468eb4b4.jpg',
    label: 'HVAC Install',
    trade: 'HVAC',
  },
  {
    url: 'https://readdy.ai/api/search-image?query=professional%20plumber%20installing%20new%20bathroom%20vanity%20and%20tap%20fixtures%20in%20modern%20Australian%20bathroom%20white%20tiles%20clean%20workmanship%20copper%20pipes%20visible%20under%20sink%20bright%20lighting&width=400&height=300&seq=sandbox-demo-img-2&orientation=landscape',
    label: 'Bathroom Plumbing',
    trade: 'Plumbing',
  },
  {
    url: 'https://readdy.ai/api/search-image?query=professional%20electrician%20installing%20new%20switchboard%20and%20circuit%20breakers%20in%20Australian%20residential%20home%20clean%20wiring%20neat%20cable%20management%20grey%20metal%20enclosure%20bright%20workshop%20lighting&width=400&height=300&seq=sandbox-demo-img-3&orientation=landscape',
    label: 'Switchboard Upgrade',
    trade: 'Electrical',
  },
  {
    url: 'https://readdy.ai/api/search-image?query=professional%20carpenter%20installing%20new%20timber%20deck%20boards%20on%20Australian%20backyard%20outdoor%20deck%20treated%20pine%20hardwood%20screws%20clean%20straight%20lines%20bright%20sunny%20day&width=400&height=300&seq=sandbox-demo-img-4&orientation=landscape',
    label: 'Timber Decking',
    trade: 'Carpentry',
  },
];

const QUOTE_OUTPUTS: Record<string, {
  jobType: string;
  complexity: string;
  duration: string;
  items: { desc: string; qty: string; unit: string; total: string }[];
  subtotal: string;
  gst: string;
  total: string;
  confidence: number;
  supplier: string;
}> = {
  HVAC: {
    jobType: 'HVAC Split System Installation',
    complexity: 'Moderate',
    duration: '1 day',
    items: [
      { desc: 'Daikin 7.1kW Split System Unit', qty: '1', unit: '$1,249.00', total: '$1,249.00' },
      { desc: 'Copper Refrigerant Pipe (3m)', qty: '2', unit: '$48.50', total: '$97.00' },
      { desc: 'Electrical Cabling & Conduit', qty: '1', unit: '$185.00', total: '$185.00' },
      { desc: 'Installation Labour (8 hrs)', qty: '8', unit: '$174.00', total: '$1,392.00' },
      { desc: 'Wall Brackets & Fixings', qty: '1', unit: '$65.00', total: '$65.00' },
    ],
    subtotal: '$2,988.00',
    gst: '$298.80',
    total: '$3,286.80',
    confidence: 94,
    supplier: 'Bunnings Warehouse',
  },
  Plumbing: {
    jobType: 'Bathroom Vanity & Tap Installation',
    complexity: 'Low–Moderate',
    duration: '4–6 hrs',
    items: [
      { desc: 'Caroma Liano II Basin Mixer', qty: '1', unit: '$289.00', total: '$289.00' },
      { desc: 'P-Trap & Waste Assembly', qty: '1', unit: '$42.00', total: '$42.00' },
      { desc: 'Flexible Hose Connectors (pair)', qty: '2', unit: '$18.50', total: '$37.00' },
      { desc: 'Plumber Labour (5 hrs)', qty: '5', unit: '$165.00', total: '$825.00' },
      { desc: 'Silicone Sealant & Consumables', qty: '1', unit: '$28.00', total: '$28.00' },
    ],
    subtotal: '$1,221.00',
    gst: '$122.10',
    total: '$1,343.10',
    confidence: 97,
    supplier: 'Reece Plumbing',
  },
  Electrical: {
    jobType: 'Switchboard Upgrade (20-Circuit)',
    complexity: 'High',
    duration: '1–2 days',
    items: [
      { desc: 'Clipsal 20-Way Switchboard', qty: '1', unit: '$385.00', total: '$385.00' },
      { desc: 'Circuit Breakers (20A, 10-pack)', qty: '2', unit: '$124.00', total: '$248.00' },
      { desc: 'RCD Safety Switches (2-pack)', qty: '1', unit: '$198.00', total: '$198.00' },
      { desc: 'Electrician Labour (10 hrs)', qty: '10', unit: '$185.00', total: '$1,850.00' },
      { desc: 'Cable, Conduit & Consumables', qty: '1', unit: '$220.00', total: '$220.00' },
    ],
    subtotal: '$2,901.00',
    gst: '$290.10',
    total: '$3,191.10',
    confidence: 91,
    supplier: 'Bunnings Warehouse',
  },
  Carpentry: {
    jobType: 'Timber Deck Construction (20m²)',
    complexity: 'Moderate–High',
    duration: '2–3 days',
    items: [
      { desc: 'Treated Pine Decking Boards (90x19)', qty: '24', unit: '$28.50', total: '$684.00' },
      { desc: 'Hardwood Joists & Bearers', qty: '8', unit: '$45.00', total: '$360.00' },
      { desc: 'Galvanised Joist Hangers (10-pack)', qty: '2', unit: '$38.00', total: '$76.00' },
      { desc: 'Carpenter Labour (18 hrs)', qty: '18', unit: '$155.00', total: '$2,790.00' },
      { desc: 'Screws, Bolts & Fixings', qty: '1', unit: '$95.00', total: '$95.00' },
    ],
    subtotal: '$4,005.00',
    gst: '$400.50',
    total: '$4,405.50',
    confidence: 89,
    supplier: 'Bunnings Warehouse',
  },
};

type Stage = 'select' | 'analyzing' | 'result';

export default function SandboxDemo() {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const [stage, setStage] = useState<Stage>('select');
  const [analyzeProgress, setAnalyzeProgress] = useState(0);
  const [analyzeStep, setAnalyzeStep] = useState(0);
  const [copied, setCopied] = useState(false);
  const intervalRef = useRef<number | null>(null);

  const analyzeSteps = [
    'Scanning job site photo...',
    'Detecting materials & scope...',
    'Fetching live Bunnings prices...',
    'Calculating labour rates (VIC)...',
    'Generating itemised quote...',
  ];

  const runAnalysis = (imgIndex: number) => {
    setSelectedImage(imgIndex);
    setStage('analyzing');
    setAnalyzeProgress(0);
    setAnalyzeStep(0);

    let step = 0;
    let prog = 0;

    intervalRef.current = window.setInterval(() => {
      prog += 4;
      setAnalyzeProgress(Math.min(prog, 100));

      const newStep = Math.floor((prog / 100) * analyzeSteps.length);
      if (newStep !== step && newStep < analyzeSteps.length) {
        step = newStep;
        setAnalyzeStep(step);
      }

      if (prog >= 100) {
        if (intervalRef.current) clearInterval(intervalRef.current);
        setTimeout(() => setStage('result'), 400);
      }
    }, 60);
  };

  const reset = () => {
    setStage('select');
    setSelectedImage(null);
    setAnalyzeProgress(0);
    setAnalyzeStep(0);
    if (intervalRef.current) clearInterval(intervalRef.current);
  };

  const currentTrade = selectedImage !== null ? DEMO_IMAGES[selectedImage].trade : null;
  const quoteData = currentTrade ? QUOTE_OUTPUTS[currentTrade] : null;

  return (
    <section className="py-24 bg-gradient-to-b from-slate-950 to-slate-900 relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-amber-500/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-5">
            <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
            <span className="text-emerald-400 font-semibold text-sm">Try It Free — No Signup Required</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Generate a Real Quote{' '}
            <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
              Right Now
            </span>
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Pick a sample job photo below. Our AI will analyse it and produce a fully itemised quote with real Australian supplier pricing — in seconds.
          </p>
          <p className="mt-3 text-xs text-slate-500 flex items-center justify-center gap-1.5">
            <i className="ri-information-line text-amber-500"></i>
            Sample output only — prices sourced from public Bunnings &amp; Reece catalogues. Always verify with your supplier before quoting.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 items-start">
          {/* LEFT — Photo Selector / Analyzer */}
          <div>
            {stage === 'select' && (
              <div>
                <p className="text-slate-300 font-semibold mb-4 flex items-center gap-2">
                  <i className="ri-camera-line text-amber-400"></i>
                  Step 1 — Choose a job photo to analyse
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {DEMO_IMAGES.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => runAnalysis(i)}
                      className="group relative rounded-2xl overflow-hidden border-2 border-white/10 hover:border-amber-500/60 transition-all duration-300 cursor-pointer hover:scale-[1.02] focus:outline-none"
                    >
                      <div className="w-full h-44 overflow-hidden">
                        <img
                          src={img.url}
                          alt={img.label}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <span className="text-white font-semibold text-sm">{img.label}</span>
                        <div className="flex items-center gap-1 mt-0.5">
                          <i className="ri-tools-line text-amber-400 text-xs"></i>
                          <span className="text-amber-300 text-xs">{img.trade}</span>
                        </div>
                      </div>
                      <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="bg-amber-500 text-white text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1 whitespace-nowrap">
                          <i className="ri-magic-line"></i> Analyse
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                <p className="text-center text-slate-500 text-xs mt-4">
                  ↑ Click any photo to instantly generate a sample quote
                </p>
              </div>
            )}

            {(stage === 'analyzing' || stage === 'result') && selectedImage !== null && (
              <div>
                <div className="relative rounded-2xl overflow-hidden border-2 border-amber-500/40 mb-5">
                  <div className="w-full h-56 overflow-hidden">
                    <img
                      src={DEMO_IMAGES[selectedImage].url}
                      alt={DEMO_IMAGES[selectedImage].label}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  {stage === 'result' && (
                    <div className="absolute top-3 right-3 bg-emerald-500 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 whitespace-nowrap">
                      <i className="ri-check-double-line"></i> Analysis Complete
                    </div>
                  )}
                  <div className="absolute bottom-3 left-3">
                    <span className="text-white font-semibold">{DEMO_IMAGES[selectedImage].label}</span>
                  </div>
                </div>

                {stage === 'analyzing' && (
                  <div className="bg-slate-800/80 rounded-2xl p-6 border border-white/10">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center">
                        <i className="ri-brain-line text-amber-400 text-xl animate-pulse"></i>
                      </div>
                      <div>
                        <p className="text-white font-semibold text-sm">AI Analysing Photo...</p>
                        <p className="text-slate-400 text-xs">{analyzeSteps[analyzeStep]}</p>
                      </div>
                    </div>

                    <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-4">
                      <div
                        className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all duration-100"
                        style={{ width: `${analyzeProgress}%` }}
                      />
                    </div>

                    <div className="space-y-2">
                      {analyzeSteps.map((step, i) => (
                        <div key={i} className="flex items-center gap-2.5">
                          <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                            i < analyzeStep ? 'bg-emerald-500' : i === analyzeStep ? 'bg-amber-500 animate-pulse' : 'bg-white/10'
                          }`}>
                            {i < analyzeStep && <i className="ri-check-line text-white text-[8px]"></i>}
                          </div>
                          <span className={`text-xs ${i <= analyzeStep ? 'text-white' : 'text-slate-500'}`}>{step}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {stage === 'result' && (
                  <div className="space-y-3">
                    <div className="bg-slate-800/80 rounded-2xl p-4 border border-emerald-500/20">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <i className="ri-shield-check-line text-emerald-400"></i>
                          <span className="text-white text-sm font-semibold">AI Confidence Score</span>
                        </div>
                        <span className="text-emerald-400 font-bold text-lg">{quoteData?.confidence}%</span>
                      </div>
                      <div className="mt-2 h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full"
                          style={{ width: `${quoteData?.confidence}%` }}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { label: 'Job Type', value: quoteData?.jobType.split(' ').slice(0, 2).join(' '), icon: 'ri-tools-line' },
                        { label: 'Complexity', value: quoteData?.complexity, icon: 'ri-bar-chart-line' },
                        { label: 'Est. Duration', value: quoteData?.duration, icon: 'ri-time-line' },
                      ].map((item, i) => (
                        <div key={i} className="bg-slate-800/80 rounded-xl p-3 border border-white/10 text-center">
                          <i className={`${item.icon} text-amber-400 text-base mb-1 block`}></i>
                          <p className="text-white text-xs font-semibold leading-tight">{item.value}</p>
                          <p className="text-slate-500 text-[10px] mt-0.5">{item.label}</p>
                        </div>
                      ))}
                    </div>

                    <button
                      onClick={reset}
                      className="w-full flex items-center justify-center gap-2 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-slate-300 text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-refresh-line"></i> Try Another Photo
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* RIGHT — Sample Output Card */}
          <div>
            {stage === 'select' && (
              <div className="flex flex-col items-center justify-center h-full min-h-[420px] bg-white/[0.02] border-2 border-dashed border-white/10 rounded-2xl p-10 text-center">
                <div className="w-16 h-16 bg-amber-500/10 rounded-2xl flex items-center justify-center mb-4">
                  <i className="ri-file-text-line text-amber-400 text-3xl"></i>
                </div>
                <p className="text-white font-semibold mb-2">Your Quote Will Appear Here</p>
                <p className="text-slate-500 text-sm">Select a job photo on the left to generate a sample itemised quote with real Australian pricing.</p>
              </div>
            )}

            {stage === 'analyzing' && (
              <div className="flex flex-col items-center justify-center h-full min-h-[420px] bg-white/[0.02] border border-white/10 rounded-2xl p-10 text-center">
                <div className="w-16 h-16 bg-amber-500/10 rounded-2xl flex items-center justify-center mb-4 animate-pulse">
                  <i className="ri-magic-line text-amber-400 text-3xl"></i>
                </div>
                <p className="text-white font-semibold mb-2">Generating Your Quote...</p>
                <p className="text-slate-500 text-sm">Hang tight — fetching live supplier prices and calculating labour rates.</p>
              </div>
            )}

            {stage === 'result' && quoteData && (
              <div className="bg-white rounded-2xl shadow-2xl overflow-hidden animate-fadeIn">
                {/* Invoice Header */}
                <div className="bg-gradient-to-r from-amber-500 to-orange-600 p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center">
                        <i className="ri-building-2-fill text-white text-xl"></i>
                      </div>
                      <div className="text-white">
                        <p className="font-bold text-base">SAMPLE QUOTE</p>
                        <p className="text-white/80 text-xs">QT-DEMO-2025</p>
                      </div>
                    </div>
                    <div className="text-right text-white">
                      <p className="text-xs text-white/80">Total (inc. GST)</p>
                      <p className="text-2xl font-bold">{quoteData.total}</p>
                    </div>
                  </div>
                  <div className="mt-3 px-3 py-1.5 bg-white/20 rounded-lg inline-flex items-center gap-1.5">
                    <i className="ri-information-line text-white/80 text-xs"></i>
                    <span className="text-white/90 text-xs">Sample output — for demonstration only</span>
                  </div>
                </div>

                {/* Job Info */}
                <div className="px-5 pt-4 pb-2 bg-slate-50 border-b border-slate-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-500">Job Description</p>
                      <p className="font-semibold text-slate-900 text-sm">{quoteData.jobType}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-500">Pricing Source</p>
                      <p className="font-semibold text-slate-700 text-xs flex items-center gap-1">
                        <i className="ri-store-3-line text-amber-500"></i> {quoteData.supplier}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Line Items */}
                <div className="px-5 py-3">
                  <div className="flex text-[10px] text-slate-400 font-semibold uppercase tracking-wider mb-2 px-1">
                    <span className="flex-1">Description</span>
                    <span className="w-8 text-center">Qty</span>
                    <span className="w-20 text-right">Unit</span>
                    <span className="w-20 text-right">Total</span>
                  </div>
                  <div className="space-y-1.5">
                    {quoteData.items.map((item, i) => (
                      <div key={i} className="flex items-center text-sm px-1 py-1.5 rounded-lg hover:bg-slate-50 transition-colors">
                        <span className="flex-1 text-slate-700 text-xs pr-2">{item.desc}</span>
                        <span className="w-8 text-center text-slate-500 text-xs">{item.qty}</span>
                        <span className="w-20 text-right text-slate-500 text-xs">{item.unit}</span>
                        <span className="w-20 text-right font-semibold text-slate-900 text-xs">{item.total}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Totals */}
                <div className="px-5 pb-4 border-t border-slate-100 pt-3 space-y-1.5">
                  <div className="flex justify-between text-sm text-slate-600">
                    <span>Subtotal</span>
                    <span>{quoteData.subtotal}</span>
                  </div>
                  <div className="flex justify-between text-sm text-slate-600">
                    <span>GST (10%)</span>
                    <span>{quoteData.gst}</span>
                  </div>
                  <div className="flex justify-between text-base font-bold text-slate-900 pt-1 border-t border-slate-200">
                    <span>Total</span>
                    <span className="text-amber-600">{quoteData.total}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="px-5 pb-5 flex gap-2">
                  <button
                    onClick={() => {
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-slate-100 hover:bg-slate-200 rounded-xl text-slate-700 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className={copied ? 'ri-check-line text-emerald-600' : 'ri-file-copy-line'}></i>
                    {copied ? 'Copied!' : 'Copy Quote'}
                  </button>
                  <Link
                    to="/invoice"
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 rounded-xl text-white text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-magic-line"></i> Create Real Quote
                  </Link>
                </div>

                <div className="px-5 pb-4 text-center">
                  <p className="text-[10px] text-slate-400">
                    * Prices from public supplier catalogues. Estimates only — verify with your supplier before quoting customers.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom CTA */}
        {stage === 'result' && (
          <div className="mt-12 text-center animate-fadeIn">
            <p className="text-slate-300 mb-4 text-lg font-medium">
              Like what you see? Create real, branded quotes for your actual jobs.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/invoice"
                className="px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold rounded-full hover:from-amber-600 hover:to-orange-700 shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-magic-line mr-2"></i>Start 7-Day Free Trial
              </Link>
              <a
                href="/video-landing/60-second-overview"
                className="px-8 py-4 bg-white/10 hover:bg-white/15 border border-white/20 text-white font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-circle-line mr-2"></i>Watch 60-Second Overview
              </a>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.5s ease-out forwards; }
      `}</style>
    </section>
  );
}
